import { useState } from "react";

import { LoadingPage } from "@hl/base-components/lib/LoadingPage";
import {
  CheckCircleFill,
  Clock,
  CoinsFill,
  LockFill,
  PauseCircleFill,
  PlayCircle,
  PlayCircleFill,
  StopCircle,
  StopCircleFill,
  DotsCircleFill,
} from "@hl/base-components/lib/assets/icons.generated";
import { STATUS_COLOR, TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import { AuctionStatus } from "@hl/shared-features/lib/apollo/graphql.generated";
import { getCurrencySymbol } from "@hl/shared-features/lib/utils/currency";
import { CardProps, Divider, Group, Paper, Stack } from "@mantine/core";

import { AuctionStats } from "apollo/graphql.generated";
import { longFormatDate } from "utils/date";
import { EditAuction } from "~/features/tokens/sections/ManageToken/Auction/EditAuction";
import SaleButtonGroup from "~/features/tokens/sections/ManageToken/Auction/SaleButtonGroup";
import { SaleData } from "~/features/tokens/sections/ManageToken/MintVector/SaleData";
import { PropertyCard } from "~/features/tokens/sections/ManageToken/properties/PropertyCard";
import { useGetManageTokenGateQuery } from "~/features/tokens/sections/ManageToken/queries.graphql.generated";
import { SaleStatus } from "~/features/tokens/sections/ManageToken/types";

import { MINT_URL } from "../../../../../config";
import SaleStatusBox, { SaleConfig } from "../SaleStatusBox";

//TODO: update to proper types here
/* eslint-disable  @typescript-eslint/no-explicit-any */
type ManageAuctionProps = {
  collection: any;
  auction: any;
  refetchCollection: any;
  refetchAuction: any;
} & Omit<CardProps, "children">;

// @ts-ignore
export const ManageAuction = ({
  collection,
  auction,
  refetchCollection,
  refetchAuction,
  ...rest
}: ManageAuctionProps) => {
  const [editAuctionOpened, setEditAuctionOpened] = useState<boolean>(false);

  const auctionUrl = MINT_URL.tokens.mint.replace(
    ":collectionId",
    collection.id
  );
  const stats: AuctionStats = auction.stats;
  const endDate = auction.end ? new Date(auction.end) : undefined;
  const displayAuction = auction && {
    id: auction.id || "",
    startDate: auction.start,
    endDate: auction.end,
    price: +auction.price,
    chainId: auction.chainId?.toString() || "",
    collectionId: collection.id || "",
    gateId: auction.gateId,
  };
  // show button if auction is over, there is at least one bid, and auction hasn't already been fulfilled
  const showFulfillButton =
    (endDate ? Date.now() > endDate.getTime() : false) &&
    stats.totalBids > 0 &&
    stats.status != AuctionStatus.Fulfilled;
  const saleStatus = convertToSaleStatus(stats.status, showFulfillButton);

  const { data: gateData, loading: gateDataLoading } =
    useGetManageTokenGateQuery({
      variables: {
        id: auction.gateId || "",
      },
      skip: !auction.gateId,
    });

  if (gateDataLoading) return <LoadingPage />;
  return (
    <>
      <Paper withBorder={false} shadow="sm" {...rest}>
        {saleStatus && (
          <>
            <SaleStatusBox
              onEditSaleClick={() => setEditAuctionOpened(true)}
              editButton="Edit auction"
              showEditButton={stats.totalBids < 1}
              showFulfillAuctionButton={showFulfillButton}
              auctionData={{
                auction,
                refetchAuction,
                refetchCollection,
                collectionId: collection.id,
              }}
              startDate={new Date(auction.start)}
              saleConfig={saleConfigMap[saleStatus]}
            />

            <Divider />
          </>
        )}
        <Stack p={16}>
          <Group grow spacing="xl" align="baseline">
            <SaleData
              label="High Bid"
              value={stats.currentBid}
              suffix={getCurrencySymbol(auction.chainId || "")}
            />
            <SaleData
              label="Reserve met"
              value={stats.totalBids > 0 ? "Yes" : "No"}
            />
          </Group>
          <PropertyCard
            icon={<CoinsFill width={20} height={20} />}
            label="Reserve price"
            value={`${auction.price} ${getCurrencySymbol(
              auction.chainId || ""
            )}`}
          />
          {saleStatus === SaleStatus.NOT_STARTED && (
            <PropertyCard
              icon={<PlayCircle width={20} height={20} />}
              label="Start date"
              value={longFormatDate(new Date(auction.start))}
            />
          )}
          <PropertyCard
            icon={<StopCircle width={20} height={20} />}
            label="End date"
            value={endDate ? longFormatDate(endDate) : "None"}
          />
          <PropertyCard
            icon={<LockFill width={20} height={20} />}
            label="Gate"
            value={
              gateData && gateData.gate.id ? gateData.gate.name : "No gate"
            }
          />
        </Stack>
        <>
          <Divider />
          <SaleButtonGroup auction={displayAuction} auctionUrl={auctionUrl} />
        </>
      </Paper>
      <EditAuction
        collectionId={collection.id}
        auction={auction}
        isOpened={editAuctionOpened}
        onClose={() => setEditAuctionOpened(false)}
        refetchCollection={refetchCollection}
        refetchAuction={refetchAuction}
      />
    </>
  );
};

const convertToSaleStatus = (
  status: AuctionStatus,
  needsFulfillment: boolean
) => {
  switch (status) {
    case AuctionStatus.Ended:
      return needsFulfillment
        ? SaleStatus.NEEDS_FULFILLMENT
        : SaleStatus.RESERVE_NOT_MET;
    case AuctionStatus.Fulfilled:
      return SaleStatus.ENDED;
    case AuctionStatus.InProgress:
      return SaleStatus.ACTIVE;
    case AuctionStatus.NotStarted:
      return SaleStatus.NOT_STARTED;
  }
};

const saleConfigMap: { [key in SaleStatus]: SaleConfig } = {
  [SaleStatus.NOT_STARTED]: {
    mintStatus: "Auction starts in :days",
    hasStarted: true,
    color: TEXT_COLOR.PRIMARY,
    icon: <Clock width={20} height={20} />,
  },
  [SaleStatus.ACTIVE]: {
    mintStatus: "Auction active",
    color: STATUS_COLOR.AFFIRMATIVE,
    icon: <PlayCircleFill width={20} height={20} />,
  },
  [SaleStatus.ENDED]: {
    mintStatus: "Auction ended",
    description: "Fulfilled",
    color: TEXT_COLOR.SECONDARY,
    icon: <CheckCircleFill width={20} height={20} />,
  },
  [SaleStatus.PAUSED]: {
    mintStatus: "Auction paused",
    color: TEXT_COLOR.SECONDARY,
    icon: <PauseCircleFill width={20} height={20} />,
  },
  [SaleStatus.SOLD_OUT]: {
    mintStatus: "Sold out",
    color: TEXT_COLOR.SECONDARY,
    icon: <CheckCircleFill width={20} height={20} />,
  },
  [SaleStatus.NEEDS_FULFILLMENT]: {
    mintStatus: "Auction ended",
    description: "Needs fulfillment",
    color: TEXT_COLOR.SECONDARY,
    icon: <DotsCircleFill width={20} height={20} />,
  },
  [SaleStatus.RESERVE_NOT_MET]: {
    mintStatus: "Auction ended",
    description: "Reserve not met",
    color: TEXT_COLOR.SECONDARY,
    icon: <StopCircleFill width={20} height={20} />,
  },
};
